import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { StylesConfig } from 'react-select';
import styled from 'styled-components';

import Button from '@components/ButtonTs';
import Input from '@components/Input';
import { SearchSelect } from '@components/SearchSelect';
import Select from '@components/Select';

import {
  AgentApiCitizenship,
  AgentTouristDraft,
  getAgentCitizenships,
} from '../../api';
import { DOCUMENT_TYPE_OPTIONS } from '../constants';
import {
  B2BOrderTourist,
  B2BTouristDocumentType,
  B2BTouristGender,
  B2BTouristVisaService,
} from '../types';

const GENDER_OPTIONS: Array<{
  title: string;
  value: B2BTouristGender;
}> = [
  { title: 'Женский', value: 'F' },
  { title: 'Мужской', value: 'M' },
];

const VISA_SERVICE_OPTIONS: Array<{
  title: string;
  value: B2BTouristVisaService;
}> = [
  { title: 'Без визовой услуги', value: 'no_visa' },
  { title: 'Полное оформление визы', value: 'visa_full' },
  { title: 'Подготовка документов', value: 'visa_docs' },
];

// Россия: ID места (Place) на бэке.
const DEFAULT_NATIONALITY_ID = '225';

interface TouristFormValues {
  name: string;
  surname: string;
  patronymic: string;
  gender: B2BTouristGender;
  birthday: string;
  nationalityId: string;
  documentType: B2BTouristDocumentType;
  passportPrefix: string;
  passportNumber: string;
  passportIssuedOn: string;
  passportExpiresOn: string;
  passportIssuer: string;
  phone: string;
  email: string;
  visaService: B2BTouristVisaService;
}

interface TouristEditorProps {
  error: string | null;
  isSaving: boolean;
  tourist?: B2BOrderTourist;
  onCancel: () => void;
  onSubmit: (tourist: AgentTouristDraft) => void;
}

const getInitialValues = (tourist?: B2BOrderTourist): TouristFormValues => ({
  name: tourist?.name || '',
  surname: tourist?.surname || '',
  patronymic: tourist?.patronymic || '',
  gender: tourist?.gender || 'F',
  birthday: tourist?.birthday || '',
  nationalityId: tourist?.nationalityId
    ? String(tourist.nationalityId)
    : DEFAULT_NATIONALITY_ID,
  documentType: tourist?.documentType || 'international_passport',
  passportPrefix: tourist?.passportPrefix || '',
  passportNumber: tourist?.passportNumber || '',
  passportIssuedOn: tourist?.passportIssuedOn || '',
  passportExpiresOn: tourist?.passportExpiresOn || '',
  passportIssuer: tourist?.passportIssuer || '',
  phone: tourist?.phone || '',
  email: tourist?.email || '',
  visaService: tourist?.visaService || 'no_visa',
});

const TouristEditor: React.FC<TouristEditorProps> = ({
  error,
  isSaving,
  tourist,
  onCancel,
  onSubmit,
}) => {
  const [form, setForm] = useState(() => getInitialValues(tourist));
  const [validationError, setValidationError] = useState<string | null>(null);
  const [citizenships, setCitizenships] = useState<AgentApiCitizenship[]>([]);

  useEffect(() => {
    let isActive = true;

    getAgentCitizenships().then((list) => {
      if (isActive) setCitizenships(list);
    });

    return () => {
      isActive = false;
    };
  }, []);

  const citizenshipOptions = useMemo(
    () =>
      citizenships.map(({ id, name_ru }) => ({
        label: name_ru,
        value: String(id),
      })),
    [citizenships]
  );
  const selectedCitizenship =
    citizenshipOptions.find(({ value }) => value === form.nationalityId) ||
    null;

  const updateField = <Field extends keyof TouristFormValues>(
    field: Field,
    value: TouristFormValues[Field]
  ) => {
    setValidationError(null);
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nationalityId = Number(form.nationalityId);
    const requiredTextFields = [
      form.name,
      form.surname,
      form.birthday,
      form.passportNumber,
    ];

    if (
      requiredTextFields.some((value) => !value.trim()) ||
      !Number.isInteger(nationalityId) ||
      nationalityId <= 0
    ) {
      setValidationError('Заполните обязательные поля');
      return;
    }

    onSubmit({
      name: form.name.trim(),
      surname: form.surname.trim(),
      patronymic: form.patronymic.trim(),
      gender: form.gender,
      birthday: form.birthday,
      nationality_id: nationalityId,
      doc_type: form.documentType,
      passport_prefix: form.passportPrefix.trim(),
      passport_number: form.passportNumber.trim(),
      passport_issued_on: form.passportIssuedOn,
      passport_expires_on: form.passportExpiresOn,
      passport_issuer: form.passportIssuer.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      visa_service: form.visaService,
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormTitle>
        {tourist ? `Редактирование: ${tourist.fullName}` : 'Новый турист'}
      </FormTitle>

      <FormGrid>
        <Field>
          <span>Фамилия *</span>
          <FormInput
            required
            disabled={isSaving}
            value={form.surname}
            onChange={(event) => updateField('surname', event.target.value)}
          />
        </Field>
        <Field>
          <span>Имя *</span>
          <FormInput
            required
            disabled={isSaving}
            value={form.name}
            onChange={(event) => updateField('name', event.target.value)}
          />
        </Field>
        <Field>
          <span>Отчество</span>
          <FormInput
            disabled={isSaving}
            value={form.patronymic}
            onChange={(event) => updateField('patronymic', event.target.value)}
          />
        </Field>

        <Field>
          <span>Пол *</span>
          <FormSelect
            disabled={isSaving}
            options={GENDER_OPTIONS}
            value={form.gender}
            onChange={(event: CustomEvent<{ value: B2BTouristGender }>) =>
              updateField('gender', event.detail.value)
            }
          />
        </Field>
        <Field>
          <span>Дата рождения *</span>
          <FormInput
            required
            disabled={isSaving}
            type="date"
            value={form.birthday}
            onChange={(event) => updateField('birthday', event.target.value)}
          />
        </Field>
        <Field as="div">
          <span>Гражданство *</span>
          <CitizenshipSelect
            isSearchable
            aria-label="Гражданство"
            customStyles={CITIZENSHIP_SELECT_STYLES}
            isDisabled={isSaving}
            options={citizenshipOptions}
            placeholder="Выберите страну"
            value={selectedCitizenship}
            onChange={(option) =>
              updateField(
                'nationalityId',
                (option as { value: string } | null)?.value || ''
              )
            }
          />
        </Field>

        <Field>
          <span>Документ *</span>
          <FormSelect
            disabled={isSaving}
            options={DOCUMENT_TYPE_OPTIONS}
            value={form.documentType}
            onChange={(event: CustomEvent<{ value: B2BTouristDocumentType }>) =>
              updateField('documentType', event.detail.value)
            }
          />
        </Field>
        <Field>
          <span>Серия</span>
          <FormInput
            disabled={isSaving}
            value={form.passportPrefix}
            onChange={(event) =>
              updateField('passportPrefix', event.target.value)
            }
          />
        </Field>
        <Field>
          <span>Номер документа *</span>
          <FormInput
            required
            disabled={isSaving}
            value={form.passportNumber}
            onChange={(event) =>
              updateField('passportNumber', event.target.value)
            }
          />
        </Field>

        <Field>
          <span>Дата выдачи</span>
          <FormInput
            disabled={isSaving}
            type="date"
            value={form.passportIssuedOn}
            onChange={(event) =>
              updateField('passportIssuedOn', event.target.value)
            }
          />
        </Field>
        <Field>
          <span>Действителен до</span>
          <FormInput
            disabled={isSaving}
            type="date"
            value={form.passportExpiresOn}
            onChange={(event) =>
              updateField('passportExpiresOn', event.target.value)
            }
          />
        </Field>
        <Field>
          <span>Кем выдан</span>
          <FormInput
            disabled={isSaving}
            value={form.passportIssuer}
            onChange={(event) =>
              updateField('passportIssuer', event.target.value)
            }
          />
        </Field>

        <Field>
          <span>Телефон</span>
          <FormInput
            disabled={isSaving}
            type="tel"
            value={form.phone}
            onChange={(event) => updateField('phone', event.target.value)}
          />
        </Field>
        <Field>
          <span>Email</span>
          <FormInput
            disabled={isSaving}
            type="email"
            value={form.email}
            onChange={(event) => updateField('email', event.target.value)}
          />
        </Field>
        <Field>
          <span>Визовая услуга</span>
          <FormSelect
            disabled={isSaving}
            options={VISA_SERVICE_OPTIONS}
            value={form.visaService}
            onChange={(event: CustomEvent<{ value: B2BTouristVisaService }>) =>
              updateField('visaService', event.detail.value)
            }
          />
        </Field>
      </FormGrid>

      {(validationError || error) && (
        <FormError>{validationError || error}</FormError>
      )}

      <FormActions>
        <Button
          disabled={isSaving}
          size="tiny"
          variant="default"
          onClick={onCancel}
        >
          Отмена
        </Button>
        <SaveButton
          disabled={isSaving}
          size="tiny"
          type="submit"
          variant="primary"
        >
          {isSaving ? 'Сохраняем…' : 'Сохранить'}
        </SaveButton>
      </FormActions>
    </Form>
  );
};

const Form = styled.form`
  background: #f8f9fa;
  border-bottom: 1px solid #e1e3e6;
  display: grid;
  gap: 12px;
  padding: 14px;
`;

const FormTitle = styled.strong`
  font-size: 13px;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
`;

const Field = styled.label`
  display: grid;
  gap: 5px;
  min-width: 0;

  > span {
    color: #676b70;
    font-size: 11px;
  }
`;

const FormInput = styled(Input)`
  border-color: #d9dce1;
  font-size: 12px;
  height: 36px;
  padding: 0 9px;
  width: 100%;
`;

const FormSelect = styled(Select)`
  border: 1px solid #d9dce1;
  font-size: 12px;
  width: 100%;
`;

const CitizenshipSelect = styled(SearchSelect)`
  font-size: 12px;
`;

// SearchSelect оформлен под поиск туров на сайте — подгоняем под поля формы.
const CITIZENSHIP_SELECT_STYLES: StylesConfig = {
  control: (base, state) => ({
    backgroundColor: state.isDisabled ? '#f5f6f7' : '#fff',
    border: '1px solid #d9dce1',
    borderRadius: 4,
    height: 36,
    minHeight: 36,
  }),
  singleValue: () => ({ color: '#17181a', fontSize: 12, fontWeight: 400 }),
  option: () => ({ fontSize: 12 }),
  valueContainer: () => ({ padding: '0 9px' }),
};

const FormError = styled.div`
  background: #fff1f0;
  border-radius: 4px;
  color: #b42318;
  font-size: 12px;
  padding: 9px 10px;
`;

const FormActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const SaveButton = styled(Button)`
  border: none;
  width: 120px;
`;

export default TouristEditor;
